import { apiClient } from '@/lib/apiClient';

export interface AssistantTurn { role: 'user' | 'assistant'; text: string }

export interface AssistantRecord { patientId?: string; consultationId?: string }

/** A confirm-first action the assistant proposes (executed by the client only after the user confirms). */
export interface AssistantAction { type: string; to: string | null; label: string }

export interface AssistantReply {
  answer: string;
  action: AssistantAction | null;
  /** Slug of the guide the answer came from, when it came from documentation. */
  guide?: string | null;
}

/**
 * Ask the in-app assistant. Sends the current page as context (no identifiers) and, when a patient
 * or consultation is open, its id — the backend gathers that record's pseudonymised clinical facts.
 * May return a proposed action for the user to confirm.
 */
export const askAssistant = async (
  question: string,
  context: string,
  role: string | null,
  record: AssistantRecord,
  history: AssistantTurn[],
): Promise<AssistantReply> => {
  const { data } = await apiClient.post<{ answer: string; action: AssistantAction | null; guide?: string | null }>('/ai/assistant', {
    question,
    context,
    role,
    patientId: record.patientId ?? null,
    consultationId: record.consultationId ?? null,
    history: history.slice(-8),
  });
  // `guide` must be copied out explicitly — this function builds its return value by hand rather
  // than returning `data`, so a field added to the response type is silently dropped until it is
  // named here. That is exactly what happened to the cited-guide button: the type said it existed,
  // the server sent it, and the UI could never render it.
  return { answer: data.answer, action: data.action, guide: data.guide ?? null };
};
